import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { 
  FaFileDownload, 
  FaCheckCircle, 
  FaUniversity, 
  FaChartLine, 
  FaShieldAlt, 
  FaCogs, 
  FaFileAlt, 
  FaUsers, 
  FaGraduationCap, 
  FaChartPie, 
  FaMedal, 
  FaSeedling, 
  FaBuilding,
  FaPiggyBank,
  FaSync,
  FaMicrochip,
  FaHandsHelping,
  FaUserFriends,
  FaPeopleArrows,
  FaLayerGroup,
  FaArrowRight,
  FaLightbulb,
} from 'react-icons/fa';
import FiscalSponsorshipModels from '../components/FiscalSponsorshipModels';
import '../styles/Services.css';
import { Link } from 'react-router-dom';
import { generateFiscalSponsorshipOverview } from '../utils/fiscalSponsorshipPDF';
import { generateDefaultSampleReceipt } from '../utils/sampleReceiptGenerator';
import { generateFiscalSponsorship101, generateFiscalSponsorshipExpert } from '../utils/educationalPDFGenerator';
import { generateScholarshipGrantOverview } from '../utils/scholarshipGrantPDF';
import { generateCausePortfolioOverview } from '../utils/causePortfolioPDF';
import { generateNILFoundationOverview, generateNILFiscalSponsorshipGuide } from '../utils/nilAthletePDF';
import { generateDynamicImpactOverview } from '../utils/dynamicImpactPDF';
import { generateCorporatePhilanthropyOverview } from '../utils/corporatePhilanthropyPDF';
import { generateDonorAdvisedFundsOverview } from '../utils/donorAdvisedFundsPDF';
import { generateSubscriptionBasedGivingOverview } from '../utils/subscriptionBasedGivingPDF';
import { generateCrowdfundingFiscalSponsorshipOverview } from '../utils/crowdfundingFiscalSponsorshipPDF';

const AcademyShowcase = () => {
  const navigate = useNavigate();

  const handleNavigation = () => {
    navigate('/academy');
  };

  return (
    <div className="academy-showcase">
      <h2>Discover Our Impact Academy</h2>
      <p className="showcase-subtitle">
        Your Gateway to Nonprofit Excellence & Fiscal Sponsorship Mastery
      </p>
      
      <div className="showcase-features">
        <div className="feature-highlight">
          <FaGraduationCap className="feature-icon" />
          <h3>Expert Knowledge Base</h3>
          <p>Access our comprehensive collection of guides, frameworks, and best practices</p>
        </div>
        
        <div className="feature-highlight">
          <FaLightbulb className="feature-icon" />
          <h3>Industry Insights</h3>
          <p>Stay ahead with cutting-edge strategies and innovative solutions</p>
        </div>
        
        <div className="feature-highlight">
          <FaChartLine className="feature-icon" />
          <h3>Growth Resources</h3>
          <p>Tools and guidance to scale your impact effectively</p>
        </div>
      </div>

      <button 
        onClick={handleNavigation} 
        className="primary-button"
        style={{ textDecoration: 'none', cursor: 'pointer', position: 'relative', zIndex: 2 }}
      >
        <span>Explore Impact Academy</span>
        <FaArrowRight className="arrow-icon" />
      </button>
      
      <p className="cta-subtitle">
        Join thousands of nonprofit leaders advancing their mission
      </p>
    </div>
  );
};

const WaitlistModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    if (isOpen) {
      const modal = document.querySelector('.waitlist-modal');
      if (modal) {
        modal.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        throw new Error('Failed to join waitlist');
      }

      setStatus('success');
      
      if (window.confetti) {
        window.confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
      }

      setTimeout(() => {
        onClose();
        setStatus('idle');
        setEmail('');
        window.location.href = '/services#technology-platform';
        const section = document.getElementById('technology-platform');
        if (section) {
          section.scrollIntoView({ behavior: 'smooth' });
        }
      }, 2000);

    } catch (error) {
      console.error('Submission error:', error);
      setStatus('error');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="waitlist-modal-overlay" onClick={onClose}>
      <div className="waitlist-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>&times;</button>
        <h3>Join the Waitlist</h3>
        <p>Be the first to know when our platform launches!</p>
        
        {status === 'success' ? (
          <div className="success-message">
            <FaCheckCircle />
            <p>You're on the list! Thank you for your interest.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              required
            />
            <button 
              type="submit" 
              disabled={status === 'loading'}
              className="waitlist-submit"
            >
              {status === 'loading' ? 'Joining...' : 'Join Waitlist'}
            </button>
          </form>
        )}
        
        {status === 'error' && (
          <p className="error-message">Something went wrong. Please try again.</p>
        )}
      </div>
    </div>
  );
};

const Services = () => {
  const [isWaitlistOpen, setIsWaitlistOpen] = useState(false);

  const openCalendly = () => {
    if (!window.Calendly) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      document.body.appendChild(script);

      script.onload = () => {
        initCalendly();
      };
    } else {
      initCalendly();
    }
  };

  const initCalendly = () => {
    if (window.Calendly) {
      window.Calendly.initPopupWidget({
        url: 'https://calendly.com/leighton-jacobus',
        prefill: {},
        text: 'Schedule a Call',
        color: '#8B5CF6',
        branding: true,
        hideEventTypeDetails: false,
        hideGdprBanner: true,
        backgroundColor: '#111827',
        textColor: '#ffffff',
        primaryColor: '#8B5CF6'
      });
    }
  };

  const services = [
    {
      category: "Fiscal Sponsorship",
      icon: <FaUniversity />,
      description: "Full-service fiscal sponsorship providing comprehensive nonprofit infrastructure and support.",
      features: [
        "Dedicated Bank Account",
        "Tax-Deductible Donation Processing",
        "Grant Management & Administration",
        "Legal & Tax Compliance",
        "Insurance Coverage",
        "Annual Form 990 Filing"
      ],
      documents: [
        {
          name: "Fiscal Sponsorship Overview",
          type: "PDF",
          url: "#",
          onClick: async (e) => {
            e.preventDefault();
            try {
              const doc = await generateFiscalSponsorshipOverview();
              if (doc) {
                doc.save('Fiscal-Sponsorship-Overview.pdf');
              } else {
                console.error('Failed to generate PDF');
              }
            } catch (error) {
              console.error('Error:', error);
            }
          }
        },
        {
          name: "Service Agreement Template",
          type: "PDF",
          url: "/apply"
        }
      ]
    },
    {
      category: "Financial Services",
      icon: <FaChartLine />,
      description: "Comprehensive financial management and reporting services for your Cause.",
      features: [
        "Dedicated Online Banking Portal",
        "Real-time Financial Visibility",
        "Expense Management",
        "Budget Planning & Tracking",
        "Donor Management System",
        "Financial Report Generation",
        "Financial Compliance Monitoring"
      ],
      documents: [
        {
          name: "Sample Tax Receipt",
          type: "PDF",
          url: "#",
          onClick: async (e) => {
            e.preventDefault();
            try {
              const doc = await generateDefaultSampleReceipt();
              doc.save('Sample-Donation-Receipt.pdf');
            } catch (error) {
              console.error('Error generating sample:', error);
            }
          }
        }
      ]
    },
    {
      category: "Compliance & Legal",
      icon: <FaShieldAlt />,
      description: "Complete legal and compliance coverage for your charitable activities.",
      features: [
        "501(c)(3) Status Coverage",
        "State & Federal Compliance",
        "Contract Review & Management",
        "Risk Management",
        "Annual Reporting",
        "Audit Support"
      ],
      documents: [
        //{
          //name: "Compliance Checklist",
          //type: "PDF",
          //url: "/documents/compliance-checklist.pdf"
        //}
      ]
    },
    {
      category: "Technology Platform",
      icon: <FaMicrochip />,
      description: "State-of-the-art technology solutions for Cause management and donor engagement.",
      features: [
        "Cause Dashboard",
        "Donor Portal",
        "Online Donation Processing",
        "Grant Management System",
        "Document Management",
        "Analytics & Reporting"
      ],
      documents: [],
      disclaimer: {
        text: "🚀 Launching Soon: Get ready for a revolutionary platform experience!",
        cta: "Join the waitlist to be first in line"
      }
    }
  ];

  const expandedServices = [
    {
      category: "Cause Portfolio Funds",
      icon: <FaChartPie />,
      description: "Mutual-fund-inspired model for diversified philanthropy, perfect for high-net-worth individuals and corporate donors.",
      features: [
        "Tax-deductible donations",
        "Single receipt for contributions",
        "Curated nonprofit portfolios",
        "Detailed allocation reports",
        "Mission-driven categories"
      ],
      documents: [
        {
          name: "Cause Portfolio Funds Overview",
          type: "PDF",
          url: "#",
          onClick: async (e) => {
            e.preventDefault();
            console.log('Generating Cause Portfolio PDF...');
            try {
              const doc = await generateCausePortfolioOverview();
              if (doc) {
                doc.save('Cause-Portfolio-Funds-Overview.pdf');
                console.log('PDF generated successfully');
              } else {
                console.error('Failed to generate PDF');
              }
            } catch (error) {
              console.error('Error generating overview:', error);
            }
          }
        }
      ]
    },
    {
      category: "Scholarship & Grant Programs",
      icon: <FaGraduationCap />,
      description: "Simplified framework for scholarship programs and emergency grants with full IRS compliance.",
      features: [
        "Selection criteria alignment",
        "Audit-ready documentation",
        "Non-discriminatory selection proof",
        "Verification documents",
        "Rigorous transparency"
      ],
      documents: [
        {
          name: "Scholarship & Grant Program Overview",
          type: "PDF",
          url: "#",
          onClick: async (e) => {
            e.preventDefault();
            try {
              const doc = await generateScholarshipGrantOverview();
              if (doc) {
                doc.save('Scholarship-Grant-Program-Overview.pdf');
              } else {
                console.error('Failed to generate PDF');
              }
            } catch (error) {
              console.error('Error generating overview:', error);
            }
          }
        }
      ]
    },
    {
      category: "NIL & Professional Athlete Foundation Management",
      icon: <FaMedal />,
      description: "Take your impact to the next level with a streamlined solution tailored for NIL college athletes and professional athletes who want to make a difference without the stress of managing a private foundation.",
      features: [
        "NIL Compliance Management - NCAA, IRS & Legal Regulations",
        "Personalized Impact Reporting & Media Relations",
        "Seamless Fund Distribution & Impact Tracking",
        "Brand Protection & Enhancement Strategy",
        "Complete Operational & Risk Management",
        "Legacy Building & Community Impact Programs"
      ],
      documents: [
        {
          name: "NIL Foundation Management Overview",
          type: "PDF",
          url: "#",
          onClick: async (e) => {
            e.preventDefault();
            try {
              const doc = await generateNILFoundationOverview();
              if (doc) {
                doc.save('NIL-Foundation-Management-Overview.pdf');
              } else {
                console.error('Failed to generate PDF');
              }
            } catch (error) {
              console.error('Error generating overview:', error);
            }
          }
        },
        {
          name: "Fiscal Sponsorship for NIL Athletes Guide",
          type: "PDF",
          url: "#",
          onClick: async (e) => {
            e.preventDefault();
            try {
              const doc = await generateNILFiscalSponsorshipGuide();
              if (doc) {
                doc.save('NIL-Fiscal-Sponsorship-Guide.pdf');
              } else {
                console.error('Failed to generate PDF');
              }
            } catch (error) {
              console.error('Error generating guide:', error);
            }
          }
        }
      ],
      additionalInfo: {
        whoItsFor: [
          "NIL College Athletes building their charitable legacy",
          "Professional Athletes seeking simplified philanthropy"
        ],
        benefits: [
          "NIL-Focused Expertise",
          "Legacy Building Support",
          "Brand Protection",
          "Compliance Assurance",
          "Operational Excellence"
        ]
      }
    },
    {
      category: "Dynamic Impact Incubators",
      icon: <FaSeedling />,
      description: "Launch and scale early-stage nonprofit projects with comprehensive support and guidance.",
      features: [
        "Back-office services",
        "Grant management",
        "Donor platform access",
        "Project vetting",
        "Scaling support"
      ],
      documents: [
        {
          name: "Dynamic Impact Incubator Overview",
          type: "PDF",
          url: "#",
          onClick: async (e) => {
            e.preventDefault();
            try {
              const doc = await generateDynamicImpactOverview();
              if (doc) {
                doc.save('Dynamic-Impact-Incubator-Overview.pdf');
              } else {
                console.error('Failed to generate PDF');
              }
            } catch (error) {
              console.error('Error generating overview:', error);
            }
          }
        }
      ]
    },
    {
      category: "Corporate Philanthropy Accelerator",
      icon: <FaBuilding />,
      description: "Outsource your company's Corporate Social Responsibility (CSR) initiatives to maximize impact while simplifying administration.",
      features: [
        "Branded cause portfolios",
        "Employee donation matching",
        "Robust impact reporting",
        "Mission alignment verification",
        "CSR program management"
      ],
      documents: [
        {
          name: "Corporate Philanthropy Accelerator Overview",
          type: "PDF",
          url: "#",
          onClick: async (e) => {
            e.preventDefault();
            try {
              const doc = await generateCorporatePhilanthropyOverview();
              if (doc) {
                doc.save('Corporate-Philanthropy-Accelerator-Overview.pdf');
              } else {
                console.error('Failed to generate PDF');
              }
            } catch (error) {
              console.error('Error generating overview:', error);
            }
          }
        }
      ]
    },
    {
      category: "Donor-Advised Funds Plus",
      icon: <FaPiggyBank />,
      description: "An enhanced model for donor-advised funds with added impact tracking and compliance oversight.",
      features: [
        "Flexible disbursement timelines",
        "Restricted-purpose oversight",
        "Impact reporting",
        "Clear donor-advisor boundaries",
        "Charitable-purpose verification"
      ],
      documents: [
        {
          name: "Donor-Advised Funds Plus Overview",
          type: "PDF",
          url: "#",
          onClick: async (e) => {
            e.preventDefault();
            try {
              const doc = await generateDonorAdvisedFundsOverview();
              if (doc) {
                doc.save('Donor-Advised-Funds-Plus-Overview.pdf');
              } else {
                console.error('Failed to generate PDF');
              }
            } catch (error) {
              console.error('Error generating overview:', error);
            }
          }
        }
      ]
    },
    {
      category: "Subscription-Based Giving",
      icon: <FaSync />,
      description: "Enable recurring, subscription-based donations to mission-aligned causes with automated processing.",
      features: [
        "Automated monthly giving",
        "Curated causes",
        "Impact reports",
        "Flexible subscription terms",
        "Transparent fund allocation"
      ],
      documents: [
        {
          name: "Subscription-Based Giving Overview",
          type: "PDF",
          url: "#",
          onClick: async (e) => {
            e.preventDefault();
            try {
              const doc = await generateSubscriptionBasedGivingOverview();
              if (doc) {
                doc.save('Subscription-Based-Giving-Overview.pdf');
              } else {
                console.error('Failed to generate PDF');
              }
            } catch (error) {
              console.error('Error generating overview:', error);
            }
          }
        }
      ]
    },
    {
      category: "Crowdfunding Fiscal Sponsorship",
      icon: <FaPeopleArrows />,
      description: "Offer fiscal sponsorship to mission-aligned crowdfunding campaigns for tax-deductible donations.",
      features: [
        "Tax-deductible receipts",
        "Compliance management",
        "Transparent allocation",
        "Mission alignment verification",
        "Campaign support services"
      ],
      documents: [
        {
          name: "Crowdfunding Fiscal Sponsorship Overview",
          type: "PDF",
          url: "#",
          onClick: async (e) => {
            e.preventDefault();
            try {
              const doc = await generateCrowdfundingFiscalSponsorshipOverview();
              if (doc) {
                doc.save('Crowdfunding-Fiscal-Sponsorship-Overview.pdf');
              } else {
                console.error('Failed to generate PDF');
              }
            } catch (error) {
              console.error('Error generating overview:', error);
            }
          }
        }
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Our Services - Cause & Impact</title>
        <meta name="description" content="Explore our comprehensive fiscal sponsorship services and solutions." />
      </Helmet>

      <div className="services-wrapper">
        <div className="glow-lines">
            <div className="glow-line"></div>
            <div className="glow-line"></div>
            <div className="glow-line"></div>
            <div className="glow-line-vertical"></div>
            <div className="glow-line-vertical"></div>
        </div>
        {/* Background Elements */}
        <div className="particles-container">
            {[...Array(20)].map((_, i) => (
                <div 
                    key={i} 
                    className="particle"
                    style={{
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`,
                        animationDuration: `${Math.random() * 20 + 10}s`,
                        animationDelay: `${Math.random() * 5}s`
                    }}
                />
            ))}
        </div>
        <div className="connected-lines">
            {[...Array(3)].map((_, i) => (
                <div 
                    key={i}
                    className="line"
                    style={{
                        top: `${25 + i * 25}%`,
                        animationDelay: `${i * 2}s`
                    }}
                />
            ))}
            {[...Array(2)].map((_, i) => (
                <div 
                    key={i}
                    className="line-vertical"
                    style={{
                        left: `${33 + i * 33}%`,
                        animationDelay: `${i * 2}s`
                    }}
                />
            ))}
        </div>
        
        {/* Hero Section */}
        <section className="services-hero">
          <div className="hero-content">
            <h1>Fiscal Sponsorship Services</h1>
            <p>
              Comprehensive solutions to help you launch, manage, and scale your charitable initiative. 
              From basic fiscal sponsorship to full-service program management, we've got you covered.
            </p>
          </div>
        </section>

        {/* Core Services Section - Moved outside services-container */}
        <section className="core-services">
          <h2>Core Services</h2>
          <p className="section-intro">
            Our fundamental fiscal sponsorship offerings designed to support your mission at every stage.
          </p>
          
          <div className="services-grid">
            {services.map((service, index) => (
              <div className="service-card" key={index}>
                <div className="service-icon">
                  <div 
                    className="service-icon"
                    onMouseMove={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const x = ((e.clientX - rect.left) / rect.width) * 100;
                        const y = ((e.clientY - rect.top) / rect.height) * 100;
                        e.currentTarget.style.setProperty('--x', `${x}%`);
                        e.currentTarget.style.setProperty('--y', `${y}%`);
                    }}
                  >
                    {service.icon}
                    <div className="ambient-light" />
                  </div>
                </div>
                <h2>{service.category}</h2>
                <p className="service-description">{service.description}</p>
                
                <div className="features-list">
                  {service.features.map((feature, fIndex) => (
                    <div className="feature-item" key={fIndex}>
                      <FaCheckCircle className="check-icon" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                {service.documents && service.documents.length > 0 && (
                  <div className="documents-section">
                    <div className="documents-list">
                      {service.documents.map((doc, dIndex) => (
                        <a 
                          href={doc.url} 
                          className="document-link" 
                          key={dIndex}
                          onClick={doc.onClick}
                        >
                          <FaFileDownload />
                          <span>{doc.name}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        <div className="services-container">
          {/* Expanded Services Section */}
          <div className="expanded-services">
            <h2>Expanded Services for Greater Impact</h2>
            <p className="section-intro">
              Additional service offerings designed to broaden our reach, diversify philanthropic 
              opportunities, and simplify giving for our donors.
            </p>
            
            <div className="expanded-services-grid">
              {expandedServices.map((service, index) => (
                <div 
                  className={`expanded-service-card ${
                    service.category.includes('Athlete') ? 'athlete-management' : ''
                  }`}
                  key={index}
                >
                  <div className="service-icon">
                    <div 
                      className="service-icon"
                      onMouseMove={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const x = ((e.clientX - rect.left) / rect.width) * 100;
                          const y = ((e.clientY - rect.top) / rect.height) * 100;
                          e.currentTarget.style.setProperty('--x', `${x}%`);
                          e.currentTarget.style.setProperty('--y', `${y}%`);
                      }}
                    >
                      {service.icon}
                      <div className="ambient-light" />
                    </div>
                  </div>
                  <h3>{service.category}</h3>
                  <p className="service-description">{service.description}</p>
                  
                  <div className="features-list">
                    {service.features.map((feature, fIndex) => (
                      <div className="feature-item" key={fIndex}>
                        <FaCheckCircle className="check-icon" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  {service.documents && service.documents.length > 0 && (
                    <div className="documents-section">
                      <h3>Resources</h3>
                      <div className="documents-list">
                        {service.documents.map((doc, dIndex) => (
                          <a 
                            href={doc.url} 
                            className="document-link" 
                            key={dIndex}
                            onClick={doc.onClick}
                            target={doc.url !== "#" ? "_blank" : undefined}
                            rel={doc.url !== "#" ? "noopener noreferrer" : undefined}
                          >
                            <FaFileDownload />
                            <span>{doc.name}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <AcademyShowcase />

          <div className="services-cta">
            <div className="cta-content">
              <h2>Ready to Get Started?</h2>
              <p>Join our community of change-makers and bring your vision to life.</p>
              <div className="cta-buttons">
                <Link to="/apply" className="cta-button primary">Apply Now</Link>
                <button 
                  onClick={openCalendly}
                  className="cta-button secondary"
                >
                  Talk to Our Team
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Services;
